/**
 * DEVLOGS-TEMP — the logger itself: filter -> rate limit -> sanitize -> queue.
 *
 * THE CONTRACT (lib/player-shell/src/player-telemetry.ts, and five other files
 * that say the same thing): never throws, never blocks, never grows unbounded,
 * no retries, fully inert when unconfigured. A venue must never stop playing
 * because telemetry is unhappy.
 *
 * There is deliberately NO console call anywhere in this module at runtime. A
 * swallowed failure stays swallowed — a logger that reports its own troubles to
 * the console is a logger that spams the surface it was installed to observe.
 */

import type { Batcher, FlushReason, BatcherStats } from "./batcher.ts";
import { LEVELS, type LevelName } from "./level.ts";
import type { NamespaceFilter } from "./level.ts";
import { NamespaceRateLimiter } from "./rate-limit.ts";
import { sanitizeError, sanitizeMessage, sanitizePayload } from "./redact.ts";
import type { MonotonicClock, SeqCounter } from "./session.ts";
import type { Level, RecordExtras, DevLogRecord } from "./types.ts";

export interface DevLogSpan {
  readonly id: string;
  end(data?: Record<string, unknown>): void;
  fail(err: unknown, data?: Record<string, unknown>): void;
}

export interface DevLogger {
  log(
    level: Level | LevelName,
    ns: string,
    evt: string,
    data?: Record<string, unknown>,
    extra?: RecordExtras,
  ): void;
  trace(ns: string, evt: string, data?: Record<string, unknown>, extra?: RecordExtras): void;
  debug(ns: string, evt: string, data?: Record<string, unknown>, extra?: RecordExtras): void;
  info(ns: string, evt: string, data?: Record<string, unknown>, extra?: RecordExtras): void;
  warn(ns: string, evt: string, data?: Record<string, unknown>, extra?: RecordExtras): void;
  error(
    ns: string,
    evt: string,
    err?: unknown,
    data?: Record<string, unknown>,
    extra?: RecordExtras,
  ): void;
  child(ns: string): DevLogger;
  span(ns: string, evt: string, data?: Record<string, unknown>): DevLogSpan;
  /** Cheap guard so an expensive payload is only built when it will be kept. */
  enabled(ns: string, level: Level): boolean;
  flush(reason?: FlushReason): void;
  stats(): BatcherStats;
}

export interface DevLoggerDeps {
  batcher: Batcher;
  clock: MonotonicClock;
  seq: SeqCounter;
  filter: NamespaceFilter;
  minLevel: Level;
  limiter?: NamespaceRateLimiter;
  saturationPerSec?: number;
  newSpanId: () => string;
}

/** The ONLY try/catch idiom in this package. */
function safeVoid(fn: () => void): void {
  try {
    fn();
  } catch {
    /* telemetry must never surface into the caller */
  }
}

export function createDevLogger(deps: DevLoggerDeps): DevLogger {
  const limiter = deps.limiter ?? new NamespaceRateLimiter(undefined, () => deps.clock.now());
  const saturationPerSec = deps.saturationPerSec ?? 200;

  // Re-entrancy guard, same shape as createErrorForwarder's `forwarding` flag:
  // catches console-capture seeing our own write and an error handler firing on
  // our own throw.
  let inLog = false;

  let windowStart = 0;
  let windowCount = 0;
  let saturatedDrops = 0;

  function saturated(now: number): boolean {
    if (now - windowStart >= 1000) {
      windowStart = now;
      windowCount = 0;
      if (saturatedDrops > 0) {
        const dropped = saturatedDrops;
        saturatedDrops = 0;
        // Report the saturation itself, at a level that survives degrade.
        emit(40, "devlog", "saturated", { dropped }, undefined, true);
      }
    }
    windowCount += 1;
    if (windowCount > saturationPerSec) {
      saturatedDrops += 1;
      return true;
    }
    return false;
  }

  function emit(
    lvl: Level,
    ns: string,
    evt: string,
    data?: Record<string, unknown>,
    extra?: RecordExtras,
    bypass = false,
  ): void {
    if (inLog) return;
    inLog = true;
    try {
      // Never log our own plumbing — guard #2 against the transport loop.
      if (!bypass && ns.startsWith("devlog")) return;

      const floor = deps.batcher.floor();
      if (floor === null && deps.batcher.stats().disabled) return;
      const minLevel = floor !== null && floor > deps.minLevel ? floor : deps.minLevel;
      if (lvl < minLevel) return;
      if (!bypass && !deps.filter.test(ns)) return;

      const now = deps.clock.now();
      if (!bypass && saturated(now)) return;

      let drop = 0;
      if (!bypass) {
        const verdict = limiter.admit(ns, now);
        if (!verdict.allow) return;
        drop = verdict.dropped;
      }

      const record: DevLogRecord = {
        seq: deps.seq.take(),
        ts: now,
        mono: deps.clock.mono(),
        lvl,
        ns,
        evt,
      };

      const msg = sanitizeMessage(extra?.msg);
      if (msg !== null) record.msg = msg;

      if (data !== undefined) {
        const sanitized = sanitizePayload(data);
        if (sanitized !== undefined && sanitized !== null) {
          record.data = sanitized as Record<string, unknown>;
        }
      }

      if (extra?.err !== undefined) {
        const err = sanitizeError(extra.err, lvl >= 50);
        if (err !== undefined) record.err = err;
      }

      if (extra?.trace !== undefined) record.trace = extra.trace;
      if (extra?.span !== undefined) record.span = extra.span;
      if (extra?.parent !== undefined) record.parent = extra.parent;
      if (extra?.reqId !== undefined) record.reqId = extra.reqId;
      if (typeof extra?.dur === "number" && Number.isFinite(extra.dur)) {
        record.dur = Math.round(extra.dur);
      }
      if (drop > 0) record.drop = drop;

      deps.batcher.push(record);
    } catch {
      /* swallowed by contract */
    } finally {
      inLog = false;
    }
  }

  function toLevel(level: Level | LevelName): Level {
    if (typeof level === "number") return level;
    return LEVELS[level] ?? LEVELS.info;
  }

  function make(prefix: string): DevLogger {
    const scoped = (ns: string): string => (prefix.length > 0 ? `${prefix}.${ns}` : ns);

    const logger: DevLogger = {
      log(level, ns, evt, data, extra) {
        safeVoid(() => emit(toLevel(level), scoped(ns), evt, data, extra));
      },
      trace(ns, evt, data, extra) {
        safeVoid(() => emit(LEVELS.trace, scoped(ns), evt, data, extra));
      },
      debug(ns, evt, data, extra) {
        safeVoid(() => emit(LEVELS.debug, scoped(ns), evt, data, extra));
      },
      info(ns, evt, data, extra) {
        safeVoid(() => emit(LEVELS.info, scoped(ns), evt, data, extra));
      },
      warn(ns, evt, data, extra) {
        safeVoid(() => emit(LEVELS.warn, scoped(ns), evt, data, extra));
      },
      error(ns, evt, err, data, extra) {
        safeVoid(() =>
          emit(LEVELS.error, scoped(ns), evt, data, { ...extra, err: err ?? extra?.err }),
        );
      },
      child(ns) {
        return make(scoped(ns));
      },
      span(ns, evt, data) {
        const id = safeCall(deps.newSpanId, "");
        const startedAt = safeCall(() => deps.clock.mono(), 0);
        safeVoid(() => emit(LEVELS.debug, scoped(ns), `${evt}.start`, data, { span: id }));
        return {
          id,
          end(endData) {
            safeVoid(() =>
              emit(LEVELS.debug, scoped(ns), `${evt}.end`, endData, {
                span: id,
                dur: safeCall(() => deps.clock.mono(), 0) - startedAt,
              }),
            );
          },
          fail(err, endData) {
            safeVoid(() =>
              emit(LEVELS.error, scoped(ns), `${evt}.fail`, endData, {
                span: id,
                err,
                dur: safeCall(() => deps.clock.mono(), 0) - startedAt,
              }),
            );
          },
        };
      },
      enabled(ns, level) {
        return safeCall(() => {
          const floor = deps.batcher.floor();
          const minLevel = floor !== null && floor > deps.minLevel ? floor : deps.minLevel;
          return level >= minLevel && deps.filter.test(scoped(ns));
        }, false);
      },
      flush(reason) {
        safeVoid(() => deps.batcher.flush(reason ?? "manual"));
      },
      stats() {
        return safeCall(() => deps.batcher.stats(), EMPTY_STATS);
      },
    };
    return logger;
  }

  return make("");
}

const EMPTY_STATS: BatcherStats = Object.freeze({
  queued: 0,
  bytes: 0,
  dropped: 0,
  sent: 0,
  failed: 0,
  batches: 0,
  inFlight: false,
  degraded: false,
  disabled: false,
  lastFailureAt: null,
});

function safeCall<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}
